<template>
  <div class="container-fluid">
    <div class="row align-items-end mt-3 mb-2">
      <div class="col-md-3">
        <b-form-group label="From">
          <b-form-input type="date" v-model="params.from"></b-form-input>
        </b-form-group>
      </div>
      <div class="col-md-3">
        <b-form-group label="To">
          <b-form-input type="date" v-model="params.to"></b-form-input>
        </b-form-group>
      </div>
      <div class="col-md-3">
        <b-form-group label="Status">
          <b-form-select v-model="params.status" :options="statusOptions"></b-form-select>
        </b-form-group>
      </div>
      <div class="col-md-3">
        <b-form-group label="Template">
          <b-form-select v-model="params.template_id" :options="templateOptions"></b-form-select>
        </b-form-group>
      </div>
      <div class="col-12 text-right mb-3">
        <b-button variant="primary" @click="applyFilters">Apply</b-button>
        <b-button variant="secondary" class="ml-2" @click="exportCsv">Export CSV</b-button>
      </div>
    </div>

    <div class="row mb-3">
      <div class="col-12">
        <b-badge variant="primary" class="mr-2 p-2">Total: {{ summary.total }}</b-badge>
        <b-badge
            v-for="(count, status) in summary.by_status"
            :key="status"
            :variant="statusVariant(status)"
            class="mr-2 p-2"
        >
          {{ status }}: {{ count }}
        </b-badge>
      </div>
    </div>

    <b-table
        :items="fields"
        :fields="headers"
        show-empty
        empty-text="No video generation jobs for the selected filters"
    >
      <template #cell(video_name)="{item}">
        {{ item.video_name || '—' }}
      </template>
      <template #cell(status)="{item}">
        <b-badge :variant="statusVariant(item.status)">{{ item.status }}</b-badge>
      </template>
      <template #cell(template_name)="{item}">
        {{ item.template_name || '—' }}
      </template>
      <template #cell(shop_id)="{item}">
        {{ item.shop_id || '—' }}
      </template>
      <template #cell(cost_cents)="{item}">
        {{ formatCost(item.cost_cents) }}
      </template>
      <template #cell(result_url)="{item}">
        <a v-if="item.result_url" :href="item.result_url" target="_blank" rel="noopener">View</a>
        <span v-else>—</span>
      </template>
      <template #cell(created_at)="{item}">
        {{ formatDateTime(item.created_at) }}
      </template>
      <template #cell(completed_at)="{item}">
        {{ formatDateTime(item.completed_at) }}
      </template>
    </b-table>

    <b-pagination
        v-model="pagination.currentPage"
        :total-rows="pagination.rows"
        :per-page="pagination.perPage"
        aria-controls="itemList"
        align="center"
        @change="handlePagination"
        v-if="pagination.total > 0"
    ></b-pagination>
  </div>
</template>
<script>
import { http } from "@/config/httpCommon"

export default {
  async created(){
    await this.fetchTemplates();
    await this.getUsage();
  },
  data(){
    return {
      video_jobs: [],
      fields: [],
      templates: [],
      summary: {
        by_status: {},
        total: 0,
      },
      statusOptions: [
        {value: '', text: 'All statuses'},
        {value: 'queued', text: 'Queued'},
        {value: 'processing', text: 'Processing'},
        {value: 'completed', text: 'Completed'},
        {value: 'failed', text: 'Failed'},
        {value: 'cancelled', text: 'Cancelled'},
      ],
      headers: [
        {key: 'id', label: 'ID'},
        {key: 'video_name', label: 'Video Name'},
        {key: 'status', label: 'Status'},
        {key: 'language', label: 'Language'},
        {key: 'template_name', label: 'Template'},
        {key: 'shop_id', label: 'Shop ID'},
        {key: 'cost_cents', label: 'Cost'},
        {key: 'result_url', label: 'Result'},
        {key: 'created_at', label: 'Created At'},
        {key: 'completed_at', label: 'Completed At'},
      ],
      pagination:{
        rows:null,
        currentPage:null,
        perPage:null,
        total:0
      },
      params: {
        from: '',
        to: '',
        status: '',
        template_id: '',
        page: 1,
      }
    }
  },
  computed: {
    templateOptions(){
      return [{ value: '', text: 'All templates' }].concat(
        this.templates.map(t => ({ value: t.id, text: t.name }))
      );
    }
  },
  methods:{
    async fetchTemplates(){
      try {
        let all = [];
        let page = 1;
        let lastPage = 1;
        do {
          let res = await http.get(`video-template?page=${page}&sortBy=name&sortOrder=ASC`);
          all = all.concat(res.data.data || []);
          lastPage = (res.data.meta && res.data.meta.last_page) ? res.data.meta.last_page : 1;
          page++;
        } while (page <= lastPage && page <= 50);
        this.templates = all;
      } catch (err) {
        // Non-fatal: the template filter just stays empty if the list can't load.
        this.templates = [];
      }
    },
    buildQuery(){
      let query = {};
      if(this.params.from) query.from = this.params.from;
      if(this.params.to) query.to = this.params.to;
      if(this.params.status) query.status = this.params.status;
      if(this.params.template_id) query.template_id = this.params.template_id;
      return query;
    },
    async getUsage() {
      this.$store.commit('set', ['showLoader', true]);
      try {
        let query = this.buildQuery();
        query.page = this.params.page;
        let params = '?' + new URLSearchParams(query).toString();
        let res = await http.get(`video-usage` + params);
        let result = res.data.result;
        this.video_jobs = result.data;
        this.fields = this.video_jobs;
        this.summary = res.data.summary || { by_status: {}, total: 0 };
        this.makePagination(result);
      } catch (err) {
        this.showError(err);
      }
      this.$store.commit('set', ['showLoader', false]);
    },
    applyFilters(){
      this.params.page = 1;
      this.getUsage();
    },
    async handlePagination(page=''){
      this.params.page = page;
      this.getUsage();
    },
    makePagination(result){
      this.pagination.currentPage = result.current_page;
      this.pagination.rows = result.total;
      this.pagination.perPage = result.per_page;
      this.pagination.total = result.total;
    },
    async exportCsv(){
      this.$store.commit('set', ['showLoader', true]);
      try {
        let params = '?' + new URLSearchParams(this.buildQuery()).toString();
        let response = await http.get(`video-usage/export` + params, { responseType: 'blob' });
        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', 'promo-video-usage.csv');
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
      } catch (err) {
        this.showError(err);
      }
      this.$store.commit('set', ['showLoader', false]);
    },
    statusVariant(status){
      switch (status) {
        case 'completed': return 'success';
        case 'processing': return 'info';
        case 'queued': return 'secondary';
        case 'failed': return 'danger';
        case 'cancelled': return 'warning';
        default: return 'secondary';
      }
    },
    formatDateTime(value){
      if(!value) return '—';
      return String(value).replace('T', ' ').substring(0, 19);
    },
    formatCost(cents){
      if(cents === null || cents === undefined || cents === '') return '—';
      return (parseInt(cents, 10) / 100).toFixed(2);
    }
  }
}
</script>
<style lang="scss" scoped>
</style>
